import React from "react";
import { Link } from "react-router-dom";
import { CountryFlag } from "@/components/black1of1/CountryFlag";
import { FoilBody } from "@/components/black1of1/FoilBody";
import { formatCalendarDay } from "@/lib/formatCalendarDay";

/**
 * Mini Black 1/1 sticker card.
 * Uses the explicit stacked-sibling FoilBody primitive so the silver
 * material is actually painted (background-blend-mode inside a
 * multi-nested wrapper was being suppressed by the browser).
 */
export function BlackParallelCard({ p, size = "grid" }) {
  const found = p.status === "found";
  const isTeam = p.sticker_type === "team_photo";
  const showApprovedImage = found && !!p.image_url;

  return (
    <Link
      to={`/black-1of1/${p.sticker_number}`}
      data-testid={`black-1of1-card-${p.sticker_number}`}
      className={[
        "group relative block rounded-xl overflow-hidden aspect-[3/4] transition-transform hover:-translate-y-0.5",
        "bg-black p-[3px] md:p-[4px]",
        found
          ? "shadow-[0_0_30px_-6px_rgba(34,197,94,0.55)] hover:shadow-[0_0_36px_-4px_rgba(34,197,94,0.85)]"
          : "shadow-[0_2px_18px_-6px_rgba(0,0,0,0.9)] hover:shadow-[0_0_30px_-4px_rgba(212,175,55,0.35)]",
      ].join(" ")}
    >
      <div className="relative w-full h-full rounded-lg overflow-hidden">
        {showApprovedImage ? (
          <img
            src={p.image_url}
            alt={p.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : found ? (
          <FoilBody countryCode={p.country_code} showOneOfOne />
        ) : (
          <img
            src="/textures/not-surfaced-card.webp"
            alt="Not surfaced"
            loading="lazy"
            data-testid={`black-1of1-not-surfaced-img-${p.sticker_number}`}
            className="absolute inset-0 w-full h-full object-contain bg-black"
          />
        )}

        {/* Info-bar transition — softer, card-caption style (shorter, less washed) */}
        <div className="absolute inset-x-0 bottom-0 h-[30%] bg-gradient-to-t from-black via-black/95 to-transparent pointer-events-none z-20" />

        {/* Status badge */}
        <div className="absolute top-2 left-2 z-30">
          {found ? (
            <span className="inline-flex items-center gap-1 text-[11px] md:text-xs font-mono font-black uppercase tracking-widest bg-[#22c55e] text-black px-2.5 py-1 rounded-sm shadow-[0_2px_10px_-2px_rgba(34,197,94,0.7)] ring-1 ring-[#16a34a]">
              <span className="w-1.5 h-1.5 rounded-full bg-black" /> Found
            </span>
          ) : (
            <span className="inline-flex items-center text-[11px] md:text-xs font-mono font-black uppercase tracking-widest bg-black text-white px-2.5 py-1 rounded-sm ring-1 ring-white/60 shadow-[0_2px_10px_-2px_rgba(0,0,0,0.9)]">
              Not Surfaced
            </span>
          )}
        </div>

        {showApprovedImage && (
          <div className="absolute top-2 right-2 z-30">
            <CountryFlag cc={p.country_code} className="w-7 h-5 rounded-sm ring-1 ring-black/70" />
          </div>
        )}

        {/* Info bar */}
        <div className="absolute inset-x-0 bottom-0 z-30 px-3 pt-3 pb-2.5">
          <div className="font-mono text-xs md:text-sm text-[#d4af37] tracking-[0.18em] font-bold uppercase">
            {p.sticker_number}
            <span className="text-white/60 font-semibold"> · {p.country_code}</span>
          </div>
          <div
            className={`mt-0.5 truncate ${
              size === "grid" ? "text-base md:text-lg" : "text-lg md:text-xl"
            } ${isTeam ? "italic" : "font-black"} text-white leading-tight`}
          >
            {p.name || "—"}
          </div>
          {found && p.first_surfaced_at && (
            <div className="mt-0.5 text-xs md:text-sm font-mono text-[#4ade80] font-bold">
              Surfaced {formatCalendarDay(p.first_surfaced_at)}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
